/**
 * As sugestões que entraram nesta atividade, e o botão "Usar sugestão" (PO,
 * 22/09/2026) — ao lado de "Da galeria", no mesmo lugar em que a pessoa já
 * está olhando a atividade. Quem administra vincula na hora; os demais pedem.
 */
import { useState } from 'react';

import { useTrip } from '../../trip/TripContext';
import { Icon } from '../../ui/icons';
import { Sheet } from '../../ui/Sheet';
import { Card, SectionLabel } from '../../ui/primitives';
import { type Sugestao, useSugestoes, useSugestoesDaAtividade, useVinculoDaSugestao } from './api';

function Escolher({ activityId, onFechar }: { activityId: string; onFechar: () => void }) {
  const { tripId } = useTrip();
  const lista = useSugestoes(tripId);
  const acoes = useVinculoDaSugestao(tripId);
  // Só o que pode ir para o roteiro agora: livre e sem pedido em aberto.
  const livres = (lista.data?.suggestions ?? []).filter((s) => s.canRequestLink);
  const erro = acoes.vincular.error as Error | null;

  return (
    <Sheet titleId="usar-sugestao-titulo" onFechar={onFechar} testId="usar-sugestao">
      <h2 className="sheet__title" id="usar-sugestao-titulo">Usar sugestão</h2>
      <Card variant="surf">
        {lista.isLoading && <p className="auth-note">Carregando…</p>}
        {lista.isSuccess && livres.length === 0 && (
          <p className="auth-note" data-testid="usar-sugestao-vazio">
            Nenhuma sugestão livre agora. Cole um link em Mais › Sugestões — ou espere a decisão dos pedidos em aberto.
          </p>
        )}
        {livres.map((s) => (
          <button
            type="button"
            key={s.id}
            className="sug__atividade-opcao"
            disabled={acoes.vincular.isPending}
            onClick={() => {
              acoes.vincular.mutate({ id: s.id, activityId });
              onFechar();
            }}
            data-testid="usar-sugestao-opcao"
          >
            <span className="sug__atividade-nome">{s.title ?? s.host}</span>
            <span className="sug__atividade-quando">{s.createdBy.me ? 'Você' : s.createdBy.name}</span>
          </button>
        ))}
      </Card>
      {erro && <p className="auth-error" role="alert">{erro.message}</p>}
      <button type="button" className="nv-btn nv-btn--secondary nv-btn--block" onClick={onFechar}>
        Fechar
      </button>
    </Sheet>
  );
}

function Item({ s }: { s: Sugestao }) {
  const { tripId } = useTrip();
  const acoes = useVinculoDaSugestao(tripId);
  const ocupado = acoes.desvincular.isPending;
  return (
    <div className="sug__na-atividade" data-testid="sugestao-na-atividade">
      <a className="sug__na-atividade-link" href={s.url} target="_blank" rel="noopener noreferrer">
        <Icon name="externo" size={16} />
        <span className="sug__na-atividade-texto">
          <span className="sug__na-atividade-titulo">{s.title ?? s.host}</span>
          <span className="sug__na-atividade-quem">{s.createdBy.me ? 'Você' : s.createdBy.name}</span>
        </span>
      </a>
      {s.linkStatus === 'pending_unlink' ? (
        <span className="sug__vinculo-chip">⏳ Saída pedida</span>
      ) : (
        s.canRequestUnlink && (
          <button type="button" className="nv-btn nv-btn--chip" disabled={ocupado} onClick={() => acoes.desvincular.mutate(s.id)} data-testid="sugestao-na-atividade-tirar">
            Tirar
          </button>
        )
      )}
    </div>
  );
}

export function SugestoesDaAtividade({ activityId }: { activityId: string }) {
  const { tripId } = useTrip();
  const lista = useSugestoesDaAtividade(tripId, activityId);
  const [escolhendo, setEscolhendo] = useState(false);
  const itens = lista.data?.suggestions ?? [];

  return (
    <>
      <div className="ativ__acoes-midia">
        <button type="button" className="nv-btn nv-btn--chip" onClick={() => setEscolhendo(true)} data-testid="ativ-usar-sugestao">
          <Icon name="externo" size={16} /> Usar sugestão
        </button>
      </div>
      {itens.length > 0 && (
        <>
          <SectionLabel>Sugestões</SectionLabel>
          <Card>
            {itens.map((s) => (
              <Item key={s.id} s={s} />
            ))}
          </Card>
        </>
      )}
      {escolhendo && <Escolher activityId={activityId} onFechar={() => setEscolhendo(false)} />}
    </>
  );
}
